import React, { useMemo } from 'react';

/**
 * RuleDerivationPanel
 * ====================
 * Renders the full derivation chain for rule-modified findings in the P4
 * CODE SUPPORT panel. Shown when a finding has non-empty rule_modifications[].
 *
 * Two sections:
 *   1. DERIVATION CHAIN — each rule_modification rendered as a numbered step
 *      showing before → after code, rule name, namespace badge, and reasoning.
 *   2. EXTRACTION CONTEXT — everything Step 20 (unified_ner_extraction) emitted:
 *      findings_detail, medications_detail, labs_detail, vitals_detail tables.
 *
 * Props:
 *   finding        — the selected CodedFinding dict (must have rule_modifications[])
 *   encounterTrace — the full encounter_trace.json object (needs step_traces[],
 *                     supporting_evidence[], coded_findings[])
 */

// ── Design tokens (mirrors HCCReviewConstants.C) ─────────────────────────────
const T = {
  navy:      '#162349',
  text:      '#1E293B',
  muted:     '#64748B',
  border:    '#E2E8F0',
  bg:        '#F8FAFC',
  blue:      '#1E40AF',
  blueBg:    '#DBEAFE',
  green:     '#15803D',
  greenBg:   '#DCFCE7',
  amber:     '#92400E',
  amberBg:   '#FFFBEB',
  purple:    '#5B21B6',
  purpleBg:  '#EDE9FE',
  red:       '#DC2626',
  mono:      "'SF Mono', 'Fira Code', 'Consolas', monospace",
};

// ── Namespace → display config ───────────────────────────────────────────────
const NS_CONFIG = {
  hcc_corroboration: {
    label:   'Corroboration Rule',
    short:   'Step 40',
    color:   T.blue,
    bg:      T.blueBg,
  },
  ner_code_corrections: {
    label:   'Code Correction',
    short:   'Step 50',
    color:   T.purple,
    bg:      T.purpleBg,
  },
  specificity_refinement: {
    label:   'Specificity Refinement',
    short:   'Step 55',
    color:   T.amber,
    bg:      T.amberBg,
  },
};

// ── Action → display ─────────────────────────────────────────────────────────
const ACTION_LABEL = {
  replaced:  { text: 'Replaced', icon: '→' },
  annotated: { text: 'Injected', icon: '+' },
  suppressed:{ text: 'Suppressed', icon: '✕' },
};


// ── Helper: extract Step 20 data from step_traces ────────────────────────────
function getStep20Data(encounterTrace) {
  if (!encounterTrace?.step_traces) return null;
  const step = encounterTrace.step_traces.find(
    s => s.step_name === 'unified_ner_extraction'
  );
  if (!step?.outputs_summary) return null;
  return step.outputs_summary;
}

// ── Helper: extract Step 25 data from step_traces ────────────────────────────
function getStep25Data(encounterTrace) {
  if (!encounterTrace?.step_traces) return null;
  const step = encounterTrace.step_traces.find(
    s => s.step_name === 'focused_ner'
  );
  if (!step?.outputs_summary) return null;
  return step.outputs_summary;
}


// ── Mini table component ─────────────────────────────────────────────────────
const cellStyle = {
  padding: '5px 8px',
  fontSize: 12,
  borderBottom: `1px solid ${T.border}`,
  color: T.text,
  verticalAlign: 'top',
};
const headerStyle = {
  ...cellStyle,
  fontSize: 11,
  fontWeight: 700,
  color: T.muted,
  letterSpacing: '0.04em',
  background: T.bg,
  position: 'sticky',
  top: 0,
};

const MiniTable = ({ columns, rows, emptyText, onPageClick }) => {
  if (!rows || rows.length === 0) {
    return (
      <div style={{ fontSize: 12, color: T.muted, fontStyle: 'italic', padding: '6px 0' }}>
        {emptyText || 'No data'}
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto', borderRadius: 4, border: `1px solid ${T.border}` }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse',
        fontSize: 12, tableLayout: 'auto',
      }}>
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key} style={{ ...headerStyle, textAlign: col.align || 'left' }}>
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : T.bg }}>
              {columns.map(col => (
                <td key={col.key} style={{ ...cellStyle, textAlign: col.align || 'left', ...(col.style || {}) }}>
                  {(() => {
                    const content = col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—');
                    if (col.key === 'page' && onPageClick && row[col.key] != null) {
                      return (
                        <span
                          onClick={() => onPageClick(row[col.key])}
                          style={{ cursor: 'pointer', color: T.blue, fontWeight: 600, textDecoration: 'underline' }}
                        >{content}</span>
                      );
                    }
                    return content;
                  })()}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


// ── Section header ───────────────────────────────────────────────────────────
const SectionHeader = ({ children, style }) => (
  <div style={{
    fontSize: 11, fontWeight: 700, color: T.muted,
    letterSpacing: '0.08em', marginBottom: 6,
    marginTop: 16, paddingBottom: 4,
    borderBottom: `1px solid ${T.border}`,
    ...style,
  }}>
    {children}
  </div>
);


// ── Derivation step card ─────────────────────────────────────────────────────
const DerivationStep = ({ mod, index, total }) => {
  const ns = NS_CONFIG[mod.rule_namespace] || {
    label: mod.rule_namespace, short: '?', color: T.muted, bg: T.bg,
  };
  const action = ACTION_LABEL[mod.action] || { text: mod.action, icon: '•' };

  const hasBeforeCode = mod.before_code && mod.before_code !== '';
  const hasAfterCode = mod.after_code && mod.after_code !== '';

  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${T.border}`,
      borderRadius: 6,
      padding: '10px 12px',
      marginBottom: total > 1 && index < total - 1 ? 0 : 0,
    }}>
      {/* Step number + namespace badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{
          width: 20, height: 20, borderRadius: '50%',
          background: ns.color, color: '#fff',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700, flexShrink: 0,
        }}>
          {index + 1}
        </span>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '1px 6px',
          borderRadius: 3, background: ns.bg, color: ns.color,
        }}>
          {ns.label}
        </span>
        <span style={{ fontSize: 11, color: T.muted }}>
          {ns.short}
        </span>
      </div>

      {/* Rule name */}
      <div style={{
        fontFamily: T.mono, fontSize: 11, color: T.muted,
        marginBottom: 6, wordBreak: 'break-all',
      }}>
        {mod.rule_name}
      </div>

      {/* Code transformation */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        marginBottom: 6, flexWrap: 'wrap',
      }}>
        {hasBeforeCode && (
          <span style={{
            fontFamily: T.mono, fontSize: 13, fontWeight: 700,
            color: T.muted, textDecoration: 'line-through',
          }}>
            {mod.before_code}
          </span>
        )}
        {hasBeforeCode && hasAfterCode && (
          <span style={{ color: ns.color, fontWeight: 700, fontSize: 14 }}>→</span>
        )}
        {hasAfterCode && (
          <span style={{
            fontFamily: T.mono, fontSize: 13, fontWeight: 700,
            color: ns.color,
          }}>
            {mod.after_code}
          </span>
        )}
        {hasAfterCode && mod.after_description && (
          <span style={{ fontSize: 12, color: T.text }}>
            {mod.after_description}
          </span>
        )}
      </div>

      {/* Reasoning */}
      {mod.rule_reasoning && (
        <div style={{
          fontSize: 12, color: T.text, lineHeight: 1.5,
          background: T.bg, borderRadius: 4, padding: '6px 8px',
          fontStyle: 'italic',
        }}>
          {mod.rule_reasoning}
        </div>
      )}
    </div>
  );
};


// ── Connector arrow between derivation steps ─────────────────────────────────
const StepConnector = () => (
  <div style={{
    display: 'flex', justifyContent: 'center', padding: '2px 0',
  }}>
    <div style={{
      width: 2, height: 16, background: T.border, position: 'relative',
    }}>
      <div style={{
        position: 'absolute', bottom: -3, left: -3,
        width: 0, height: 0,
        borderLeft: '4px solid transparent',
        borderRight: '4px solid transparent',
        borderTop: `6px solid ${T.border}`,
      }} />
    </div>
  </div>
);


// ── Column definitions for the evidence tables ───────────────────────────────

const FINDINGS_COLUMNS = [
  {
    key: 'icd10_code', title: 'Code',
    style: { fontFamily: T.mono, fontWeight: 700, whiteSpace: 'nowrap' },
  },
  { key: 'description', title: 'Description' },
  {
    key: 'page', title: 'Page',
    align: 'center',
    render: v => v != null ? `p.${v}` : '—',
  },
  {
    key: 'section', title: 'Section',
    render: v => (v || '').replace(/_/g, ' '),
  },
  {
    key: 'support_status', title: 'Status',
    render: v => {
      const map = {
        gospel:      { color: T.green, label: 'Gospel' },
        supported:   { color: T.blue,  label: 'Supported' },
        unsupported: { color: T.red,   label: 'Unsupported' },
      };
      const info = map[v] || { color: T.muted, label: v || '—' };
      return <span style={{ color: info.color, fontWeight: 600, fontSize: 11 }}>{info.label}</span>;
    },
  },
];

const MEDS_COLUMNS = [
  { key: 'text', title: 'Drug' },
  {
    key: 'page', title: 'Page', align: 'center',
    render: v => v != null ? `p.${v}` : '—',
  },
  {
    key: 'section', title: 'Section',
    render: v => (v || '').replace(/_/g, ' '),
  },
];

const LABS_COLUMNS = [
  { key: 'text', title: 'Lab Detail' },
  {
    key: 'page', title: 'Page', align: 'center',
    render: v => v != null ? `p.${v}` : '—',
  },
  {
    key: 'section', title: 'Section',
    render: v => (v || '').replace(/_/g, ' '),
  },
];

const VITALS_COLUMNS = [
  { key: 'text', title: 'Vitals' },
  {
    key: 'page', title: 'Page', align: 'center',
    render: v => v != null ? `p.${v}` : '—',
  },
  {
    key: 'section', title: 'Section',
    render: v => (v || '').replace(/_/g, ' '),
  },
];


// ═══════════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════════

const RuleDerivationPanel = ({ finding, encounterTrace , onPageClick}) => {
  const mods = finding?.rule_modifications;
  const hasRules = mods && mods.length > 0;

  const step20 = useMemo(() => getStep20Data(encounterTrace), [encounterTrace]);
  const step25 = useMemo(() => getStep25Data(encounterTrace), [encounterTrace]);

  // All hooks must run unconditionally — guard after hooks only
  const sortedMods = useMemo(() => {
    if (!mods || mods.length === 0) return [];
    return [...mods].sort((a, b) => {
      if (a.applied_at && b.applied_at) return a.applied_at.localeCompare(b.applied_at);
      return 0;
    });
  }, [mods]);

  const enrichedFindings = useMemo(() => {
    const details = step20?.findings_detail || [];
    const traceCF = encounterTrace?.coded_findings || [];
    return details.map(d => {
      const match = traceCF.find(cf => cf.icd10_code === d.icd10_code);
      const page = match?.evidence?.[0]?.page ?? null;
      return { ...d, page };
    });
  }, [step20, encounterTrace]);

  const step25Findings = useMemo(() => {
    if (!step25?.per_section) return [];
    const results = [];
    for (const sec of step25.per_section) {
      if (sec.findings) {
        for (const f of sec.findings) {
          results.push({
            icd10_code: f.icd10_code || f.code,
            description: f.description || '',
            section: sec.section_type || sec.section || '',
            page: f.page ?? sec.page ?? null,
            support_status: f.support_status || '',
          });
        }
      }
    }
    return results;
  }, [step25]);

  const medsDetail = step20?.medications_detail || [];
  const labsDetail = step20?.labs_detail || [];
  const vitalsDetail = step20?.vitals_detail || [];

  // Guard — after all hooks and derivations
  if (!hasRules) return null;

  return (
    <div style={{ padding: '12px 14px', overflowY: 'auto', height: '100%' }}>

      {/* ── Section 1: Derivation Chain ─────────────────────────────── */}
      <SectionHeader style={{ marginTop: 0 }}>DERIVATION CHAIN</SectionHeader>

      <div style={{ marginBottom: 8 }}>
        {sortedMods.map((mod, i) => (
          <React.Fragment key={mod.modification_id || i}>
            <DerivationStep mod={mod} index={i} total={sortedMods.length} />
            {i < sortedMods.length - 1 && <StepConnector />}
          </React.Fragment>
        ))}
      </div>

      {/* Final code callout */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px', background: T.greenBg,
        borderRadius: 4, border: `1px solid #86EFAC`,
        marginBottom: 4,
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.green, letterSpacing: '0.04em' }}>
          FINAL CODE
        </span>
        <span style={{ fontFamily: T.mono, fontSize: 14, fontWeight: 700, color: T.green }}>
          {finding.icd10_code}
        </span>
        <span style={{ fontSize: 12, color: T.text }}>
          {finding.description}
        </span>
      </div>


      {/* ── Section 2: Extraction Context (Step 20) ────────────────── */}
      <SectionHeader>EXTRACTION CONTEXT · STEP 20</SectionHeader>

      <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>
        All codes, medications, labs, and vitals extracted during initial NER — the
        inputs that downstream rules operated on.
      </div>

      {/* Codes table */}
      <div style={{ marginBottom: 12 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: T.navy,
          letterSpacing: '0.04em', marginBottom: 4,
        }}>
          CODES ({enrichedFindings.length})
        </div>
        <MiniTable
          columns={FINDINGS_COLUMNS}
          rows={enrichedFindings}
          emptyText="No codes extracted"
          onPageClick={onPageClick}
        />
      </div>

      {/* Medications table */}
      <div style={{ marginBottom: 12 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: T.navy,
          letterSpacing: '0.04em', marginBottom: 4,
        }}>
          MEDICATIONS ({medsDetail.length})
        </div>
        <MiniTable
          columns={MEDS_COLUMNS}
          rows={medsDetail}
          emptyText="No medications extracted"
          onPageClick={onPageClick}
        />
      </div>

      {/* Labs table */}
      <div style={{ marginBottom: 12 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: T.navy,
          letterSpacing: '0.04em', marginBottom: 4,
        }}>
          LABS ({labsDetail.length})
        </div>
        <MiniTable
          columns={LABS_COLUMNS}
          rows={labsDetail}
          emptyText="No labs extracted"
        />
      </div>

      {/* Vitals table */}
      <div style={{ marginBottom: 12 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: T.navy,
          letterSpacing: '0.04em', marginBottom: 4,
        }}>
          VITALS ({vitalsDetail.length})
        </div>
        <MiniTable
          columns={VITALS_COLUMNS}
          rows={vitalsDetail}
          emptyText="No vitals extracted"
          onPageClick={onPageClick}
        />
      </div>

      {/* Step 25 focused NER (if it ran) */}
      {step25Findings.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <SectionHeader>EXTRACTION CONTEXT · STEP 25 (FOCUSED NER)</SectionHeader>
          <MiniTable
            columns={FINDINGS_COLUMNS}
            rows={step25Findings}
            emptyText="No focused NER findings"
            onPageClick={onPageClick}
          />
        </div>
      )}
    </div>
  );
};

export default RuleDerivationPanel;